import React from 'react';
import { Tv, Globe, Share2, Loader2 } from 'lucide-react';
import { SimpleStory, StoryStatus } from '../services/simpleStore';
import {
  Platform,
  LanguageCode,
  LANGUAGE_NAMES,
  StoryGridData,
  PlatformLanguageConfig,
} from '../types';

// =============================================================================
// Story Grid Component - Phase 3
// =============================================================================
// Replaces StoryGroupCard with a platform-language matrix view.
// Shows all versions of a story group in a grid layout.
// =============================================================================

interface StoryGridProps {
  gridData: StoryGridData;
  onCellClick: (platform: Platform, language: LanguageCode, story: SimpleStory | null) => void;
  onGenerateClick: (platform: Platform, language: LanguageCode) => void;
  compact?: boolean; // Compact mode for list view
  generatingCells?: Array<{ platform: Platform; language: LanguageCode }> | null;
}

// Platforms to show in grid (excludes 'radio' and 'all')
const GRID_PLATFORMS: Platform[] = ['tv', 'web', 'social'];

// Languages in display order
const GRID_LANGUAGES: LanguageCode[] = ['fa-AF', 'ps-AF', 'eng'];

// Short language labels for header
const LANGUAGE_SHORT_LABELS: Record<LanguageCode, string> = {
  'fa-AF': 'Dari',
  'ps-AF': 'Pashto',
  'eng': 'English',
};

// Platform icons
const PLATFORM_ICONS: Record<Platform, React.ReactNode> = {
  tv: <Tv className="h-4 w-4" />,
  web: <Globe className="h-4 w-4" />,
  social: <Share2 className="h-4 w-4" />,
  radio: null,
  all: null,
};

// Platform labels
const PLATFORM_LABELS: Record<Platform, string> = {
  tv: 'TV',
  web: 'Web',
  social: 'Social',
  radio: 'Radio',
  all: 'All',
};

// Status colors
const STATUS_COLORS: Record<StoryStatus, string> = {
  approved: 'text-green-600 dark:text-green-500',
  reviewed: 'text-amber-600 dark:text-amber-500',
  pending_review: 'text-blue-600 dark:text-blue-500',
  ai_review: 'text-purple-600 dark:text-purple-500',
  ready: 'text-blue-600 dark:text-blue-500',
  published: 'text-green-700 dark:text-green-400',
  draft: 'text-zinc-500 dark:text-zinc-400',
  archived: 'text-zinc-400 dark:text-zinc-500',
};

// Status labels
const STATUS_LABELS: Record<StoryStatus, string> = {
  approved: 'Approved',
  reviewed: 'Reviewed',
  pending_review: 'Pending Review',
  ai_review: 'AI Review',
  ready: 'Ready',
  published: 'Published',
  draft: 'Draft',
  archived: 'Archived',
};

/**
 * Check if a platform-language combination is allowed
 */
function isCellDisabled(
  platform: Platform,
  language: LanguageCode,
  config: PlatformLanguageConfig[]
): boolean {
  const entry = config.find(c => c.platform === platform && c.language === language);
  return entry ? !entry.allowed : false;
}

/**
 * Get cell background color based on state
 */
function getCellBackground(
  story: SimpleStory | null,
  isDisabled: boolean,
  isCanonical: boolean
): string {
  if (isDisabled) {
    return 'bg-zinc-100 dark:bg-zinc-800/50';
  }
  if (isCanonical && story) {
    return 'bg-blue-50 dark:bg-blue-900/20';
  }
  return 'bg-white dark:bg-zinc-900';
}

/**
 * Check if a story has no content (empty draft awaiting AI generation).
 */
function isEmptyDraft(story: SimpleStory): boolean {
  return !story.headline && !story.body && !story.platformMetadata.script;
}

const StoryGrid: React.FC<StoryGridProps> = ({
  gridData,
  onCellClick,
  onGenerateClick,
  compact = false,
  generatingCells,
}) => {
  const { canonicalStory, grid, platformLanguageConfig, title } = gridData;

  // Render a single cell in the grid
  const renderCell = (platform: Platform, language: LanguageCode) => {
    const story = grid[platform]?.[language] || null;
    const isDisabled = isCellDisabled(platform, language, platformLanguageConfig);
    const isCanonical = story?.isCanonical || false;
    const isGenerating = generatingCells?.some(
      c => c.platform === platform && c.language === language
    ) || false;

    // Disabled cell (e.g., English TV)
    if (isDisabled) {
      return (
        <div
          key={`${platform}-${language}`}
          className="w-[120px] min-w-[120px] p-3 text-center text-zinc-400 dark:text-zinc-600 bg-zinc-100 dark:bg-zinc-800/50 cursor-not-allowed"
          title={`${LANGUAGE_SHORT_LABELS[language]} ${PLATFORM_LABELS[platform]} is not available`}
        >
          <span className="text-xs italic">N/A</span>
        </div>
      );
    }

    // Cell is currently generating a derivative (with or without existing draft row)
    if (isGenerating) {
      return (
        <div
          key={`${platform}-${language}`}
          className="w-[120px] min-w-[120px] p-3 text-center bg-purple-50 dark:bg-purple-900/20 transition-colors"
        >
          <div className="flex items-center justify-center gap-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-purple-500" />
            <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
              Generating
            </span>
          </div>
        </div>
      );
    }

    // Empty draft (row exists but no content) - "Pending" state
    if (story && isEmptyDraft(story) && !isCanonical) {
      return (
        <div
          key={`${platform}-${language}`}
          className="w-[120px] min-w-[120px] p-3 text-center bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 cursor-pointer transition-colors"
          onClick={() => onGenerateClick(platform, language)}
          title="Empty draft — click to generate content"
        >
          <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
            Pending
          </span>
        </div>
      );
    }

    // Empty cell (no story yet) - clickable to generate
    if (!story) {
      return (
        <div
          key={`${platform}-${language}`}
          className="w-[120px] min-w-[120px] p-3 text-center bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 cursor-pointer transition-colors"
          onClick={() => onGenerateClick(platform, language)}
        >
          <span className="text-zinc-400 dark:text-zinc-500 hover:text-blue-500 dark:hover:text-blue-400 text-sm">
            -
          </span>
        </div>
      );
    }

    // Story exists with content - show status
    const bgColor = getCellBackground(story, isDisabled, isCanonical);

    return (
      <div
        key={`${platform}-${language}`}
        className={`w-[120px] min-w-[120px] p-3 text-center ${bgColor} hover:bg-zinc-50 dark:hover:bg-zinc-800 cursor-pointer transition-colors`}
        onClick={() => onCellClick(platform, language, story)}
      >
        <div className="flex items-center justify-center gap-1">
          <span className={`text-sm font-medium ${STATUS_COLORS[story.status]}`}>
            {STATUS_LABELS[story.status]}
          </span>
          {isCanonical && (
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${STATUS_COLORS[story.status]} bg-current`} />
          )}
        </div>
      </div>
    );
  };

  // Compact mode for list view - just show the grid without header
  if (compact) {
    return (
      <div className="border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden">
        {/* Header row with languages */}
        <div className="grid grid-cols-[100px_repeat(3,120px)] bg-zinc-50 dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700">
          <div className="p-2" />
          {GRID_LANGUAGES.map(lang => (
            <div key={lang} className="w-[120px] p-2 text-center text-xs font-medium text-zinc-600 dark:text-zinc-400">
              {LANGUAGE_SHORT_LABELS[lang]}
            </div>
          ))}
        </div>

        {/* Platform rows */}
        {GRID_PLATFORMS.map(platform => (
          <div
            key={platform}
            className="grid grid-cols-[100px_repeat(3,120px)] border-b border-zinc-200 dark:border-zinc-700 last:border-b-0"
          >
            <div className="p-2 flex items-center gap-1.5 text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800">
              {PLATFORM_ICONS[platform]}
              <span className="text-xs font-medium">{PLATFORM_LABELS[platform]}</span>
            </div>
            {GRID_LANGUAGES.map(lang => renderCell(platform, lang))}
          </div>
        ))}
      </div>
    );
  }

  // Full mode with title header
  return (
    <div className="border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden bg-white dark:bg-zinc-900">
      {/* Title header */}
      <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700">
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 truncate">
          {title}
        </h3>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
          Canonical: {LANGUAGE_SHORT_LABELS[canonicalStory.language]} {PLATFORM_LABELS[canonicalStory.platform]}
          {canonicalStory.status === 'approved' && (
            <span className="text-green-600 dark:text-green-500 ml-1">
              - Approved
            </span>
          )}
        </p>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        {/* Header row with languages */}
        <div className="grid grid-cols-[100px_repeat(3,120px)] bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
          <div className="p-3" />
          {GRID_LANGUAGES.map(lang => (
            <div
              key={lang}
              className="w-[120px] min-w-[120px] p-3 text-center text-sm font-medium text-zinc-600 dark:text-zinc-400"
            >
              {LANGUAGE_SHORT_LABELS[lang]}
            </div>
          ))}
        </div>

        {/* Platform rows */}
        {GRID_PLATFORMS.map(platform => (
          <div
            key={platform}
            className="grid grid-cols-[100px_repeat(3,120px)] border-b border-zinc-200 dark:border-zinc-700 last:border-b-0"
          >
            {/* Platform label */}
            <div className="p-3 flex items-center gap-2 text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800/50">
              {PLATFORM_ICONS[platform]}
              <span className="text-sm font-medium">{PLATFORM_LABELS[platform]}</span>
            </div>
            {/* Language cells */}
            {GRID_LANGUAGES.map(lang => renderCell(platform, lang))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StoryGrid;
